import { ClassifierResponseDto, IssueStatus, KeyValueDto } from '@/types/webapi';
import { firstCharToLower } from '@/helpers/StringHelpers';
import { IIssueStatusType, Classifiers } from '@/features/Issues/types';

const getStatusInfo = (status: number) => {
    switch (status) {
        case IssueStatus.InProgress:
            return {
                color: 'warning',
                icon: 'mdi-progress-clock'
            };
        case IssueStatus.ClaimProcessed:
        case IssueStatus.Processed:
        case IssueStatus.Closed:
            return {
                color: 'primary',
                icon: 'mdi-check-circle'
            };
        case IssueStatus.Reopened:
            return {
                color: 'error',
                icon: 'mdi-refresh'
            };
        case IssueStatus.New:
        case IssueStatus.Registered:
            return {
                color: 'info',
                icon: 'mdi-file-document-outline'
            };
        default:
            return {
                color: 'grey',
                icon: 'mdi-help-circle'
            };
    }
};

const getIssueStatusType = (status: IssueStatus | undefined): IIssueStatusType => {
    if (!status) {
        return {
            icon: 'mdi-progress_activity',
            text: 'validation.loading',
            color: 'default'
        } as IIssueStatusType;
    }

    const statusValue = IssueStatus[status];
    const statusInfo = getStatusInfo(Number(statusValue));
    const statusText = `issuesStatuses.${firstCharToLower(status.toString())}`;

    return {
        ...statusInfo,
        text: statusText
    };
};

const mapData = <T extends { name?: string; key?: string }, R extends Record<string, any>>(
    data: T[] | undefined,
    keyProp: keyof T,
    valueProp: keyof T
): R =>
    data?.reduce((acc: R, item: T) => {
        const key = item[keyProp];
        if (item && key) {
            (acc as Record<string, any>)[key as string] = item[valueProp];
        }
        return acc;
    }, {} as R) ?? ({} as R);

const mapClassifierData = (classifiersData: ClassifierResponseDto[] | undefined): Classifiers =>
    mapData<ClassifierResponseDto, Classifiers>(classifiersData, 'name', 'valueList');

const mapKeyValueData = (keyValueData: KeyValueDto[] | undefined): Record<string, string> =>
    mapData<KeyValueDto, Record<string, string>>(keyValueData, 'key', 'value');

export { getStatusInfo, getIssueStatusType, mapClassifierData, mapKeyValueData };
